import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Filter,
  MapPin,
  DollarSign,
  X,
  BadgeCheck,
  AlignLeft,
  Loader2,
  Search as SearchIcon,
} from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";
import { motion } from "framer-motion";
import { API_ORIGIN } from "../config/api";

const API = API_ORIGIN;

export default function CoachCards() {
  const location = useLocation();
  const navigate = useNavigate();

  // Meta
  const [specialties, setSpecialties] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);
  const [categories, setCategories] = useState([]);

  // Data
  const [coaches, setCoaches] = useState([]);

  // UI
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  // ✓ Default sort: Newest → Oldest
  const [sortOption, setSortOption] = useState("Newest");

  // Load-more
  const STEP = 18;
  const [showCount, setShowCount] = useState(STEP);

  // ---------- Filters (IDs + NAMES in URL + FullName text) ----------
  const filters = useMemo(() => {
    const q = new URLSearchParams(location.search);
    const toInt = (k) => {
      const v = q.get(k);
      return v ? Number(v) : null;
    };
    const toStr = (k) => (q.get(k) || "").trim();

    return {
      // by ID
      CategoryId: toInt("CategoryId"),
      SpecialityId: toInt("SpecialityId"),
      CountryId: toInt("CountryId"),
      CityId: toInt("CityId"),
      AreaId: toInt("AreaId"),

      // by NAME (from Home page, QuickFilters, etc.)
      CategoryName: toStr("CategoryName"),
      SpecialtyName: toStr("Specialty") || toStr("SpecialityName"),
      CountryName: toStr("Country"),
      CityName: toStr("City"),
      AreaName: toStr("Area"),

      // others
      Gender: toStr("Gender"),
      FullName: toStr("FullName"),
      Format: toStr("Format"),
    };
  }, [location.search]);

  // Controlled FullName (for debounce + suggestions)
  const [fullNameLocal, setFullNameLocal] = useState(filters.FullName);
  useEffect(() => setFullNameLocal(filters.FullName), [filters.FullName]);

  // ---------- helpers ----------
  const normalizeName = (s) =>
    (s || "")
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  const resolveIdByName = (arr, name) => {
    if (!name) return null;
    const target = normalizeName(name);
    const found = (arr || []).find((x) => normalizeName(x?.name) === target);
    return found?.id ?? null;
  };

  const n = (v, fallback = 0) => {
    const x = Number(v);
    return Number.isFinite(x) ? x : fallback;
  };

  // ✓ Robust "CreatedAtUtc" parsing (supports a few possible field names)
  const getCreatedAtMs = (coach) => {
    const raw =
      coach?.createdAtUtc ??
      coach?.CreatedAtUtc ??
      coach?.createdAt ??
      coach?.CreatedAt ??
      null;

    if (!raw) return 0;
    const d = new Date(raw);
    const ms = d.getTime();
    return Number.isNaN(ms) ? 0 : ms;
  };

  // ✓ Robust review/rating fields (so sorting actually works even if API naming differs)
  const getReviewsCount = (coach) =>
    n(
      coach?.numReviews ??
        coach?.reviewCount ??
        coach?.reviewsCount ??
        coach?.totalReviews ??
        coach?.reviews ??
        0,
      0
    );

  const getRating = (coach) =>
    n(
      coach?.rating ??
        coach?.avgRating ??
        coach?.averageRating ??
        coach?.rate ??
        0,
      0
    );

  const getBookings = (coach) =>
    n(coach?.numBookings ?? coach?.bookingsCount ?? coach?.totalBookings ?? 0, 0);

  // ✓ effective IDs (this is the key fix)
  const effective = useMemo(() => {
    const CategoryId =
      filters.CategoryId || resolveIdByName(categories, filters.CategoryName);

    const SpecialityId =
      filters.SpecialityId ||
      resolveIdByName(specialties, filters.SpecialtyName);

    const CountryId =
      filters.CountryId || resolveIdByName(countries, filters.CountryName);

    const CityId = filters.CityId || resolveIdByName(cities, filters.CityName);

    const AreaId = filters.AreaId || resolveIdByName(areas, filters.AreaName);

    return { CategoryId, SpecialityId, CountryId, CityId, AreaId };
  }, [
    filters.CategoryId,
    filters.SpecialityId,
    filters.CountryId,
    filters.CityId,
    filters.AreaId,
    filters.CategoryName,
    filters.SpecialtyName,
    filters.CountryName,
    filters.CityName,
    filters.AreaName,
    categories,
    specialties,
    countries,
    cities,
    areas,
  ]);

  // ---------- Meta loaders ----------
  useEffect(() => {
    (async () => {
      try {
        const catsJson = await (await fetch(`${API}/api/Categories`)).json();
        const cats = Array.isArray(catsJson) ? catsJson : [];
        setCategories(cats);
      } catch (e) {
        console.error("Categories load failed:", e);
      }
    })();
  }, []);

  // Load specialties based on effective CategoryId (works for ID or CategoryName)
  useEffect(() => {
    (async () => {
      try {
        let url = `${API}/api/Specialities`;
        if (effective.CategoryId) url += `?categoryId=${effective.CategoryId}`;
        const specsJson = await (await fetch(url)).json();
        setSpecialties(Array.isArray(specsJson) ? specsJson : []);
      } catch (e) {
        console.error("Specialities load failed:", e);
      }
    })();
  }, [effective.CategoryId]);

  useEffect(() => {
    (async () => {
      try {
        const data = await (await fetch(`${API}/api/Country`)).json();
        setCountries(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Countries load failed:", e);
      }
    })();
  }, []);

  // Load cities based on effective CountryId (ID or CountryName)
  useEffect(() => {
    (async () => {
      try {
        if (!effective.CountryId) {
          setCities([]);
          return;
        }
        const data = await (
          await fetch(`${API}/api/City/country/${effective.CountryId}`)
        ).json();
        setCities(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Cities load failed:", e);
      }
    })();
  }, [effective.CountryId]);

  // Load areas based on effective CityId (ID or CityName)
  useEffect(() => {
    (async () => {
      try {
        if (!effective.CityId) {
          setAreas([]);
          return;
        }
        const data = await (
          await fetch(`${API}/api/Area/city/${effective.CityId}`)
        ).json();
        setAreas(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Areas load failed:", e);
      }
    })();
  }, [effective.CityId]);

  // ---------- Subscription filter helper ----------
  const isActiveSubscription = (coach) => {
    const now = new Date();

    const expiryStr =
      coach.subscriptionExpiresAtUtc || coach.currentPeriodEndUtc || null;

    if (expiryStr) {
      const expiry = new Date(expiryStr);
      if (!Number.isNaN(expiry.getTime()) && expiry > now) return true;
    }

    if (typeof coach.subscriptionTier === "number" && coach.subscriptionTier > 0)
      return true;

    if (
      coach.subscriptionStatus !== undefined &&
      coach.subscriptionStatus !== null &&
      coach.subscriptionStatus !== 0 &&
      coach.subscriptionStatus !== "None"
    ) {
      return true;
    }

    return false;
  };

  // ---------- Coaches loader (send IDs to API, even if URL has names) ----------
  useEffect(() => {
    (async () => {
      setErr("");
      setLoading(true);

      const p = new URLSearchParams();
      if (effective.CategoryId) p.append("CategoryId", String(effective.CategoryId));
      if (effective.SpecialityId) p.append("SpecialityId", String(effective.SpecialityId));
      if (effective.CountryId) p.append("CountryId", String(effective.CountryId));
      if (effective.CityId) p.append("CityId", String(effective.CityId));
      if (effective.AreaId) p.append("AreaId", String(effective.AreaId));
      if (filters.Gender) p.append("Gender", filters.Gender);
      if (filters.FullName) p.append("FullName", filters.FullName);
      if (filters.Format) p.append("Format", filters.Format);

      try {
        const url = `${API}/api/Coaches/Search?${p.toString()}`;
        const { data } = await axios.get(url);
        setCoaches(Array.isArray(data) ? data : []);
        setShowCount(STEP);
      } catch (e) {
        console.error("Coaches load failed:", e);
        setErr("Something went wrong loading coaches. Please try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, [
    effective.CategoryId,
    effective.SpecialityId,
    effective.CountryId,
    effective.CityId,
    effective.AreaId,
    filters.Gender,
    filters.FullName,
    filters.Format,
  ]);

  // ---------- Sorting (only active subscription coaches) ----------
  const sortedCoaches = useMemo(() => {
    const activeOnly = coaches.filter(isActiveSubscription);
    const arr = [...activeOnly];

    switch (sortOption) {
      // ✓ DEFAULT listing: Newest → Oldest by CreatedAtUtc
      case "Newest":
      case "Default":
        return arr.sort((a, b) => getCreatedAtMs(b) - getCreatedAtMs(a));

      case "Price":
        return arr.sort((a, b) => (a.price ?? Infinity) - (b.price ?? Infinity));

      case "PriceDesc":
        return arr.sort((a, b) => (b.price ?? -Infinity) - (a.price ?? -Infinity));

      // ✓ FIX: reviews/rating use robust getters
      case "ReviewsDesc":
        return arr.sort((a, b) => getReviewsCount(b) - getReviewsCount(a));

      case "Rating":
        return arr.sort((a, b) => getRating(b) - getRating(a));

      case "Popularity":
        return arr.sort((a, b) => getBookings(b) - getBookings(a));

      default:
        return arr.sort((a, b) => getCreatedAtMs(b) - getCreatedAtMs(a));
    }
  }, [coaches, sortOption]);

  const visible = sortedCoaches.slice(0, showCount);
  const canLoadMore = showCount < sortedCoaches.length;

  // ---------- URL helpers ----------
  const applyQuery = (q) => navigate(`/Coaches/Search?${q.toString()}`);

  // ✓ when user uses ID dropdowns, remove NAME params to avoid conflicts
  const clearNameParams = (q) => {
    q.delete("CategoryName");
    q.delete("Specialty");
    q.delete("SpecialityName");
    q.delete("Country");
    q.delete("City");
    q.delete("Area");
    return q;
  };

  const setIdFilter = (key, val) => {
    const q = clearNameParams(new URLSearchParams(location.search));

    if (val) q.set(key, String(val));
    else q.delete(key);

    if (key === "CountryId") {
      q.delete("CityId");
      q.delete("AreaId");
    }
    if (key === "CityId") q.delete("AreaId");

    applyQuery(q);
  };

  const setTextFilter = (key, val) => {
    const q = new URLSearchParams(location.search);
    const trimmed = (val || "").trim();
    if (trimmed) q.set(key, trimmed);
    else q.delete(key);
    applyQuery(q);
  };

  const removeFilter = (key) => {
    const q = new URLSearchParams(location.search);

    // remove both styles
    if (key === "Category") {
      q.delete("CategoryId");
      q.delete("CategoryName");
    } else if (key === "Specialty") {
      q.delete("SpecialityId");
      q.delete("Specialty");
      q.delete("SpecialityName");
    } else if (key === "Country") {
      q.delete("CountryId");
      q.delete("Country");
      q.delete("CityId");
      q.delete("City");
      q.delete("AreaId");
      q.delete("Area");
    } else if (key === "City") {
      q.delete("CityId");
      q.delete("City");
      q.delete("AreaId");
      q.delete("Area");
    } else if (key === "Area") {
      q.delete("AreaId");
      q.delete("Area");
    } else {
      q.delete(key);
      if (key === "CountryId") {
        q.delete("CityId");
        q.delete("AreaId");
      }
      if (key === "CityId") q.delete("AreaId");
    }

    applyQuery(q);
  };

  // ---------- FullName typeahead ----------
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestItems, setSuggestItems] = useState([]);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debounceRef = useRef(null);
  const suggestDebounceRef = useRef(null);
  const desktopInputRef = useRef(null);
  const mobileInputRef = useRef(null);

  const fetchSuggestions = async (q) => {
    try {
      const { data } = await axios.get(`${API}/api/Coaches/Names`, {
        params: { q: q || "" },
      });
      if (Array.isArray(data)) setSuggestItems(data);
      else setSuggestItems([]);
    } catch (e) {
      console.error("Names fetch failed:", e);
      setSuggestItems([]);
    }
  };

  const openAndFetch = (val) => {
    const hasQuery = (val || "").trim().length >= 1;
    if (!hasQuery) {
      setSuggestOpen(false);
      setSuggestItems([]);
      return;
    }
    setSuggestOpen(true);
    if (suggestDebounceRef.current) clearTimeout(suggestDebounceRef.current);
    suggestDebounceRef.current = setTimeout(() => fetchSuggestions(val), 200);
  };

  const onChangeFullName = (val) => {
    setFullNameLocal(val);
    openAndFetch(val);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setTextFilter("FullName", val);
    }, 400);
  };

  const onSubmitFullName = (e) => {
    e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setTextFilter("FullName", fullNameLocal);
    setSuggestOpen(false);
  };

  const clearFullName = () => {
    setFullNameLocal("");
    setTextFilter("FullName", "");
    setSuggestOpen(false);
  };

  const selectSuggestion = (item) => {
    setFullNameLocal(item.fullName);
    setTextFilter("FullName", item.fullName);
    setSuggestOpen(false);
    setActiveIdx(-1);
  };

  useEffect(() => {
    const onDocClick = (e) => {
      if (
        !e.target.closest?.(".name-search-wrap") &&
        !e.target.closest?.(".name-suggest-popover")
      ) {
        setSuggestOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const onKeyDownName = (e) => {
    if (!suggestOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (activeIdx >= 0 && activeIdx < suggestItems.length) {
        e.preventDefault();
        selectSuggestion(suggestItems[activeIdx]);
      }
    } else if (e.key === "Escape") {
      setSuggestOpen(false);
    }
  };

  // ---------- Chips labels ----------
  const nameById = (arr, id) => arr.find((x) => x.id === id)?.name || "";

  const chipItems = [
    filters.FullName && { key: "FullName", label: `"${filters.FullName}"` },

    (effective.CategoryId || filters.CategoryName) && {
      key: "Category",
      label: filters.CategoryName || nameById(categories, effective.CategoryId),
    },
    (effective.SpecialityId || filters.SpecialtyName) && {
      key: "Specialty",
      label:
        filters.SpecialtyName || nameById(specialties, effective.SpecialityId),
    },
    (effective.CountryId || filters.CountryName) && {
      key: "Country",
      label: filters.CountryName || nameById(countries, effective.CountryId),
    },
    (effective.CityId || filters.CityName) && {
      key: "City",
      label: filters.CityName || nameById(cities, effective.CityId),
    },
    (effective.AreaId || filters.AreaName) && {
      key: "Area",
      label: filters.AreaName || nameById(areas, effective.AreaId),
    },

    filters.Gender && { key: "Gender", label: filters.Gender },
    filters.Format && { key: "Format", label: filters.Format },
  ].filter(Boolean);

  // ---------- UI Helpers ----------
  const CardSkeleton = () => (
    <div className="rounded-[22px] border border-gray-200 shadow-sm overflow-hidden">
      <Skeleton className="h-80 w-full" />
      <div className="p-5 space-y-3">
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-3 w-2/3" />
        <Skeleton className="h-3 w-1/3" />
        <Skeleton className="h-10 w-28 ml-auto" />
      </div>
    </div>
  );

  const CoachCard = ({ coach }) => (
    <motion.div
      key={coach.id}
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/coach/${coach.id}`)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate(`/coach/${coach.id}`);
        }
      }}
      className="group cursor-pointer bg-white rounded-[22px] border border-gray-200 shadow-md hover:shadow-2xl transition-all duration-300 flex flex-col overflow-hidden focus:outline-none focus:ring-2 focus:ring-sky-500"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Image */}
      <div className="h-80 bg-gray-50 flex items-center justify-center relative">
        {coach.profileImage ? (
          <img
            src={coach.profileImage}
            alt={coach.fullName}
            className="max-h-full max-w-full object-contain p-4"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="text-gray-400 italic">No Profile Image</div>
        )}
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-sky-500 via-indigo-500 to-fuchsia-500" />
      </div>

      {/* Info */}
      <div className="p-5 flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900 truncate">
            {coach.fullName}
          </h3>
          {coach.isVerified && (
            <span className="flex items-center gap-1 text-xs font-semibold text-sky-600">
              <BadgeCheck className="w-4 h-4" /> Verified
            </span>
          )}
        </div>

        {coach.specialtyName && (
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <span className="h-2 w-2 rounded-full bg-sky-500" />
            <span className="truncate">{coach.specialtyName}</span>
          </div>
        )}

        <div className="flex items-center gap-2 text-sm text-gray-600">
          <MapPin className="w-4 h-4 text-indigo-500" />
          <span className="truncate">
            {coach.cityName}
            {coach.areaName ? `, ${coach.areaName}` : ""}
          </span>
        </div>

        <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
          <DollarSign className="w-4 h-4 text-green-500" />
          <span>
            {coach.price && Number(coach.price) > 0
              ? `Start From ${coach.price} AED / Session`
              : "Price on request"}
          </span>
        </div>

        {coach.description && (
          <p className="text-sm text-gray-600 clamp-1 flex gap-1">
            <AlignLeft className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
            {coach.description}
          </p>
        )}

        <div className="mt-3 flex justify-end">
          <Button
            className="rounded-full bg-gradient-to-r from-sky-600 to-indigo-600 text-white"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/coach/${coach.id}`);
            }}
          >
            View Profile
          </Button>
        </div>
      </div>
    </motion.div>
  );

  // ---------- Render ----------
  return (
    <div className="relative">
      <style>{`
        .clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>

      {/* Sticky filter/search bar */}
      <div className="sticky top-16 z-40 bg-white/90 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-wrap items-center gap-3">
          {/* Full name search (desktop) */}
          <form
            onSubmit={onSubmitFullName}
            className="name-search-wrap relative flex-1 min-w-[220px] max-w-[520px]"
            role="search"
            aria-label="Search coaches by full name"
          >
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              ref={desktopInputRef}
              type="text"
              placeholder="Search by full name…"
              className="w-full pl-9 pr-9 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
              value={fullNameLocal}
              onChange={(e) => onChangeFullName(e.target.value)}
              onFocus={() => openAndFetch(fullNameLocal)}
              onKeyDown={onKeyDownName}
            />
            {fullNameLocal ? (
              <button
                type="button"
                aria-label="Clear name"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-slate-100"
                onClick={clearFullName}
              >
                <X className="w-4 h-4 text-slate-500" />
              </button>
            ) : null}

            {suggestOpen && (
              <div className="name-suggest-popover absolute z-50 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-72 overflow-auto">
                {suggestItems.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-slate-500">
                    No matches
                  </div>
                ) : (
                  suggestItems.map((it, idx) => (
                    <button
                      key={it.id}
                      type="button"
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${
                        idx === activeIdx ? "bg-slate-100" : ""
                      }`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() =>
                        selectSuggestion({ id: it.id, fullName: it.fullName })
                      }
                    >
                      {it.fullName}
                    </button>
                  ))
                )}
              </div>
            )}
          </form>

          {/* Mobile Filter Button */}
          <Button
            variant="outline"
            className="sm:hidden ml-auto"
            onClick={() => setMobileOpen(true)}
          >
            <Filter className="w-4 h-4 mr-2" /> Filters
          </Button>

          {/* Desktop Filters */}
          <div className="hidden sm:flex flex-wrap gap-2 ml-auto">
            <select
              className="border rounded-lg p-2 bg-white"
              value={effective.CategoryId ?? ""}
              onChange={(e) => setIdFilter("CategoryId", e.target.value || null)}
            >
              <option value="">Category</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>

            <select
              className="border rounded-lg p-2 bg-white"
              value={effective.SpecialityId ?? ""}
              onChange={(e) =>
                setIdFilter("SpecialityId", e.target.value || null)
              }
            >
              <option value="">Specialty</option>
              {specialties.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>

            <select
              className="border rounded-lg p-2 bg-white"
              value={effective.CountryId ?? ""}
              onChange={(e) => setIdFilter("CountryId", e.target.value || null)}
            >
              <option value="">Country</option>
              {countries.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>

            <select
              className="border rounded-lg p-2 bg-white"
              value={effective.CityId ?? ""}
              onChange={(e) => setIdFilter("CityId", e.target.value || null)}
            >
              <option value="">City</option>
              {cities.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>

            <select
              className="border rounded-lg p-2 bg-white"
              value={effective.AreaId ?? ""}
              onChange={(e) => setIdFilter("AreaId", e.target.value || null)}
            >
              <option value="">Area</option>
              {areas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>

            <select
              className="border rounded-lg p-2 bg-white"
              value={filters.Gender}
              onChange={(e) => setTextFilter("Gender", e.target.value || "")}
            >
              <option value="">Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </div>

          {/* Active chips */}
          <div className="w-full flex flex-wrap gap-2">
            {chipItems.map((chip) => (
              <Badge
                key={chip.key + "-" + chip.label}
                variant="outline"
                className="flex items-center gap-1"
              >
                {chip.label}
                <X
                  className="w-3 h-3 cursor-pointer"
                  onClick={() => removeFilter(chip.key)}
                />
              </Badge>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 z-[60] bg-black/40"
            onClick={() => setMobileOpen(false)}
          />
          <div
            className="fixed inset-y-0 left-0 z-[61] w-80 max-w-[85%] bg-white shadow-2xl flex flex-col"
            role="dialog"
            aria-modal="true"
          >
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-bold text-lg">Filters</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileOpen(false)}
              >
                Close
              </Button>
            </div>

            <div className="px-4 py-3 border-b border-gray-100">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/Coaches/Search`)}
              >
                Clear all
              </Button>
            </div>

            <div className="px-4 py-4 space-y-4 flex-1 overflow-y-auto">
              {/* Full name search (mobile) */}
              <div className="name-search-wrap relative">
                <label className="block text-sm font-medium mb-1">
                  Search by full name
                </label>
                <div className="relative">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    ref={mobileInputRef}
                    type="text"
                    placeholder="e.g., Mohamed Ari"
                    className="w-full pl-9 pr-9 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    value={fullNameLocal}
                    onChange={(e) => onChangeFullName(e.target.value)}
                    onFocus={() => openAndFetch(fullNameLocal)}
                    onKeyDown={onKeyDownName}
                  />
                  {fullNameLocal ? (
                    <button
                      type="button"
                      aria-label="Clear name"
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-slate-100"
                      onClick={clearFullName}
                    >
                      <X className="w-4 h-4 text-slate-500" />
                    </button>
                  ) : null}
                </div>

                {suggestOpen && (
                  <div className="name-suggest-popover absolute z-50 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-72 overflow-auto">
                    {suggestItems.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-slate-500">
                        No matches
                      </div>
                    ) : (
                      suggestItems.map((it, idx) => (
                        <button
                          key={it.id}
                          type="button"
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${
                            idx === activeIdx ? "bg-slate-100" : ""
                          }`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() =>
                            selectSuggestion({ id: it.id, fullName: it.fullName })
                          }
                        >
                          {it.fullName}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                <select
                  className="w-full border rounded-lg p-2 bg-white"
                  value={effective.CategoryId ?? ""}
                  onChange={(e) =>
                    setIdFilter("CategoryId", e.target.value || null)
                  }
                >
                  <option value="">All Categories</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Specialty */}
              <div>
                <label className="block text-sm font-medium mb-1">Specialty</label>
                <select
                  className="w-full border rounded-lg p-2 bg-white"
                  value={effective.SpecialityId ?? ""}
                  onChange={(e) =>
                    setIdFilter("SpecialityId", e.target.value || null)
                  }
                >
                  <option value="">All Specialty</option>
                  {specialties.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Country, City, Area */}
              {[
                { key: "CountryId", label: "Country", options: countries, value: effective.CountryId ?? "" },
                { key: "CityId", label: "City", options: cities, value: effective.CityId ?? "" },
                { key: "AreaId", label: "Area", options: areas, value: effective.AreaId ?? "" },
              ].map(({ key, label, options, value }) => (
                <div key={key}>
                  <label className="block text-sm font-medium mb-1">{label}</label>
                  <select
                    className="w-full border rounded-lg p-2 bg-white"
                    value={value}
                    onChange={(e) => setIdFilter(key, e.target.value || null)}
                  >
                    <option value="">{`All ${label}`}</option>
                    {options.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.name}
                      </option>
                    ))}
                  </select>
                </div>
              ))}

              {/* Gender */}
              <div>
                <label className="block text-sm font-medium mb-1">Gender</label>
                <select
                  className="w-full border rounded-lg p-2 bg-white"
                  value={filters.Gender}
                  onChange={(e) => setTextFilter("Gender", e.target.value || "")}
                >
                  <option value="">All Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
            </div>

            <div className="p-4 border-t border-gray-200">
              <Button className="w-full" onClick={() => setMobileOpen(false)}>
                Close
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Sort */}
      <div className="max-w-7xl mx-auto px-4 py-3 flex justify-end">
        <select
          className="border rounded-lg p-2 bg-white"
          value={sortOption}
          onChange={(e) => setSortOption(e.target.value)}
        >
          <option value="Newest">Sort by: Newest</option>
          <option value="Price">Price (Low → High)</option>
          <option value="PriceDesc">Price (High → Low)</option>
          <option value="ReviewsDesc">Most Reviewed</option>
          <option value="Rating">Top Rated</option>
          <option value="Popularity">Popular</option>
        </select>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-7xl px-4 pt-2">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl md:text-2xl font-extrabold tracking-tight text-slate-900">
            Find Your Coach
          </h2>
          {loading && (
            <span className="inline-flex items-center text-sm text-slate-600">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
            </span>
          )}
        </div>

        {err && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            {err}
          </div>
        )}

        {loading && coaches.length === 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
        ) : visible.length === 0 ? (
          <div className="rounded-[22px] border border-slate-200 bg-white p-10 text-center shadow-[0_10px_30px_rgba(2,6,23,0.06)]">
            <p className="text-slate-700">
              No coaches match your filters yet. Try clearing filters or searching
              a nearby city.
            </p>
            <div className="mt-4">
              <Button onClick={() => navigate(`/Coaches/Search`)} variant="outline">
                Clear all filters
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((coach) => (
                <CoachCard key={coach.id} coach={coach} />
              ))}
            </div>
            {canLoadMore && (
              <div className="flex justify-center gap-3 py-8">
                <Button
                  variant="outline"
                  onClick={() =>
                    setShowCount((c) => Math.min(c + STEP, sortedCoaches.length))
                  }
                >
                  Load more
                </Button>
                <Button onClick={() => setShowCount(sortedCoaches.length)}>
                  Show all
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
